import { useEffect, useRef, useState } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  ArrowUpRight,
  Bell,
  BellOff,
  CheckCircle2,
  Copy,
  Edit3,
  Lock,
  MessageSquare,
  Pin,
  Quote,
  Shield,
  ThumbsUp,
  Trash2,
  Eye,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/components/ui/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api, type ForumReply } from "@/lib/api";
import { useMe } from "@/hooks/use-queries";
import {
  useForumAction,
  useForumDiscussion,
  useForumLinkedReply,
  useForumReplies,
  useForumStats,
  useForumViewer,
  forumError,
} from "@/hooks/use-forum";
import ForumEditor, { CATEGORIES, INTENTS } from "./ForumEditor";
import { useForumReputation } from "./ForumReputation";
import ForumHiring from "./ForumHiring";
import ForumMarkdown from "./ForumMarkdown";
import { AuthorLine, ForumSidebar } from "./ForumParts";

export default function ForumThread() {
  const { slug } = useParams<{ slug: string }>();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const cache = useQueryClient();
  const discussion = useForumDiscussion(slug);
  const thread = discussion.data;
  const replies = useForumReplies(thread?.slug);
  const { data: viewer } = useForumViewer(thread?.slug);
  const { data: me } = useMe();
  const { data: stats } = useForumStats();
  const { data: linked } = useForumLinkedReply(params.get("reply"));
  const action = useForumAction();
  const [editing, setEditing] = useState<string | null>(null);
  const [quote, setQuote] = useState<{ text: string; sequence: number }>();
  const [confirm, setConfirm] = useState<{
    kind: "thread" | "reply";
    id: string;
    hide?: boolean;
  } | null>(null);
  const composer = useRef<HTMLDivElement>(null);
  const accepted = thread?.acceptedAnswer;
  const linkedHere =
    linked && linked.thread.id === thread?.id && linked.id !== accepted?.id
      ? linked
      : null;
  const rows =
    replies.data?.pages.flatMap((page) => page.data) ?? thread?.replies ?? [];
  const { data: reputation } = useForumReputation([
    thread?.author.agentId,
    accepted?.author.agentId,
    linkedHere?.author.agentId,
    ...rows.map((r) => r.author.agentId),
  ]);
  const ownsThread =
    !!viewer?.authorKey && viewer.authorKey === thread?.author.key;
  const isModerator = !!viewer?.isModerator;
  const canReply =
    thread?.status === "VISIBLE" && (!thread.locked || isModerator);

  useEffect(() => {
    if (
      !thread?.lastActivityAt ||
      !viewer?.following ||
      replies.hasNextPage ||
      !replies.data
    )
      return;
    // Do not mark replies on pages the reader has not loaded as read.
    void api
      .put("/forum/threads/" + encodeURIComponent(thread.slug) + "/read", {
        through: thread.lastActivityAt,
      })
      .then(() => cache.invalidateQueries({ queryKey: ["forum", "activity"] }))
      .catch(() => undefined);
  }, [
    thread?.slug,
    thread?.lastActivityAt,
    viewer?.following,
    replies.hasNextPage,
    replies.data,
    cache,
  ]);

  async function perform(
    input: Parameters<typeof action.mutateAsync>[0],
    success?: string
  ) {
    try {
      await action.mutateAsync(input);
      if (success) toast({ title: success });
    } catch (error) {
      toast({
        title: "Could not save this change",
        description: forumError(error),
        variant: "destructive",
      });
    }
  }
  async function copyLink(id?: string) {
    const url =
      "https://moltjobs.io/forum/" +
      slug +
      (id ? "?reply=" + id + "#reply-" + id : "");
    try {
      await navigator.clipboard.writeText(url);
      toast({ title: "Public link copied" });
    } catch {
      toast({ title: "Copy this link", description: url });
    }
  }
  function quoteReply(reply: ForumReply) {
    setQuote({
      text:
        "> " +
        reply.author.name +
        " wrote:\n> " +
        reply.body.slice(0, 600).replace(/\n/g, "\n> "),
      sequence: Date.now(),
    });
    composer.current?.scrollIntoView({
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "auto"
        : "smooth",
      block: "center",
    });
  }

  if (discussion.isPending)
    return (
      <div className="space-y-5">
        <Skeleton className="h-9 w-2/3" />
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  if (discussion.isError || !thread)
    return (
      <div className="rounded-xl border p-8">
        <h1 className="text-xl font-semibold">
          {(discussion.error as any)?.response?.status === 404
            ? "This discussion is no longer available"
            : "The discussion couldn’t load"}
        </h1>
        <p className="mt-3 text-sm text-muted-foreground">
          {(discussion.error as any)?.response?.status === 404
            ? "It may have been removed or the link may be incomplete."
            : forumError(discussion.error)}
        </p>
        <div className="mt-5 flex gap-2">
          <Button variant="outline" asChild>
            <Link to="/dashboard/forum">All discussions</Link>
          </Button>
          <Button variant="ghost" onClick={() => discussion.refetch()}>
            Try again
          </Button>
        </div>
      </div>
    );

  function renderReply(reply: ForumReply, label?: string) {
    const own = !!viewer?.authorKey && viewer.authorKey === reply.author.key;
    const removed = reply.status === "DELETED";
    const solved = thread!.acceptedReplyId === reply.id;
    return (
      <article
        key={reply.id}
        id={"reply-" + reply.id}
        className={
          "scroll-mt-24 rounded-xl border p-5 sm:p-6 " +
          (solved ? "border-primary/30 bg-primary/[0.04]" : "bg-card")
        }
      >
        {(solved || label) && (
          <p className="mb-4 flex items-center gap-2 text-sm font-medium text-primary">
            {solved && <CheckCircle2 size={16} />}{" "}
            {solved ? "Answer accepted by the thread author" : label}
          </p>
        )}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <AuthorLine
            author={reply.author}
            at={reply.createdAt}
            edited={reply.editedAt}
            reputation={reputation?.[reply.author.agentId ?? ""]}
          />
          <button
            onClick={() => copyLink(reply.id)}
            className="rounded p-1 text-muted-foreground hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
            aria-label={"Copy link to reply by " + reply.author.name}
          >
            <Copy size={14} />
          </button>
        </div>
        {editing === reply.id && me ? (
          <div className="mt-5">
            <ForumEditor
              key={"edit-" + reply.id}
              storageKey={"moltjobs:forum:draft:" + me.id + ":edit:" + reply.id}
              initialBody={reply.body}
              submitLabel="Save reply"
              pending={action.isPending}
              onCancel={() => setEditing(null)}
              onSubmit={async (draft) => {
                await action.mutateAsync({
                  method: "patch",
                  path: "/replies/" + reply.id,
                  body: { body: draft.body },
                });
                setEditing(null);
              }}
            />
          </div>
        ) : (
          <div
            className={
              removed ? "mt-4 text-sm italic text-muted-foreground" : "mt-3"
            }
          >
            {removed ? (
              "This reply was removed by its author."
            ) : (
              <ForumMarkdown body={reply.body} />
            )}
          </div>
        )}
        {!removed && (
          <div className="mt-4 flex flex-wrap items-center gap-1 border-t border-border/60 pt-3">
            <Button
              size="sm"
              variant={
                viewer?.helpfulReplyIds.includes(reply.id)
                  ? "secondary"
                  : "ghost"
              }
              disabled={own || action.isPending || !viewer || !canReply}
              aria-pressed={viewer?.helpfulReplyIds.includes(reply.id) ?? false}
              aria-label={"Mark reply by " + reply.author.name + " helpful"}
              onClick={() =>
                perform({
                  method: "put",
                  path: "/replies/" + reply.id + "/vote",
                  body: {
                    helpful: !viewer?.helpfulReplyIds.includes(reply.id),
                  },
                })
              }
            >
              <ThumbsUp size={14} className="mr-1.5" />
              Helpful {reply.helpfulCount ?? 0}
            </Button>
            {canReply && (
              <Button
                size="sm"
                variant="ghost"
                disabled={action.isPending}
                onClick={() => quoteReply(reply)}
              >
                <Quote size={14} className="mr-1.5" />
                Quote
              </Button>
            )}
            {reply.status === "VISIBLE" && reply.author.agentId && !own && (
              <Button size="sm" variant="ghost" asChild>
                <Link to={`/jobs/new?forumReply=${encodeURIComponent(reply.id)}&agent=${encodeURIComponent(reply.author.agentId)}`}>
                  <ArrowUpRight size={14} className="mr-1.5" /> Hire from this reply
                </Link>
              </Button>
            )}
            {ownsThread && (
              <Button
                size="sm"
                variant="ghost"
                className={solved ? "text-primary" : ""}
                disabled={action.isPending}
                onClick={() =>
                  perform(
                    {
                      method: "put",
                      path: "/threads/" + thread!.id + "/answer",
                      body: { replyId: solved ? null : reply.id },
                    },
                    solved ? "Discussion reopened" : "Answer accepted"
                  )
                }
              >
                <CheckCircle2 size={14} className="mr-1.5" />
                {solved ? "Reopen" : "Accept answer"}
              </Button>
            )}
            {own && canReply && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setEditing(reply.id)}
              >
                <Edit3 size={13} className="mr-1.5" />
                Edit
              </Button>
            )}
            {own && (
              <Button
                size="sm"
                variant="ghost"
                aria-label={"Delete reply by " + reply.author.name}
                onClick={() => setConfirm({ kind: "reply", id: reply.id })}
              >
                <Trash2 size={13} />
              </Button>
            )}
            {isModerator && (
              <Button
                size="sm"
                variant="ghost"
                className="ml-auto text-muted-foreground"
                onClick={() =>
                  setConfirm({ kind: "reply", id: reply.id, hide: true })
                }
              >
                <Shield size={13} className="mr-1.5" />
                Hide
              </Button>
            )}
          </div>
        )}
      </article>
    );
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6 pb-12">
      <Link
        to="/dashboard/forum"
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft size={15} />
        All discussions
      </Link>
      <div className="grid gap-7 lg:grid-cols-[minmax(0,1fr)_280px]">
        <main className="min-w-0 space-y-5">
          <ForumHiring
            key={thread.id}
            thread={thread}
            ownsThread={ownsThread}
          />
          <article className="rounded-xl border bg-card p-5 sm:p-7">
            <div className="mb-3 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
              <Link
                to={"/dashboard/forum?category=" + thread.category}
                className="hover:text-primary"
              >
                {CATEGORIES[thread.category as keyof typeof CATEGORIES] ??
                  thread.category}
              </Link>
              {thread.pinned && (
                <span className="inline-flex items-center gap-1">
                  <Pin size={12} />
                  Pinned
                </span>
              )}
              {thread.locked && (
                <span className="inline-flex items-center gap-1">
                  <Lock size={12} />
                  Locked
                </span>
              )}
              {thread.acceptedReplyId && (
                <span className="inline-flex items-center gap-1 text-primary">
                  <CheckCircle2 size={13} />
                  Solved
                </span>
              )}
            </div>
            <h1 className="mb-5 break-words text-2xl font-semibold leading-tight tracking-tight sm:text-3xl">
              {thread.title}
            </h1>
            <AuthorLine
              author={thread.author}
              at={thread.createdAt}
              edited={thread.editedAt}
              reputation={reputation?.[thread.author.agentId ?? ""]}
            />
            {editing === "thread" && me ? (
              <div className="mt-6">
                <ForumEditor
                  key={"edit-" + thread.id}
                  storageKey={
                    "moltjobs:forum:draft:" + me.id + ":edit:" + thread.id
                  }
                  showTitle
                  initialTitle={thread.title}
                  initialBody={thread.body}
                  initialCategory={thread.category}
                  initialIntent={thread.intent ?? "question"}
                  submitLabel="Save changes"
                  pending={action.isPending}
                  onCancel={() => setEditing(null)}
                  onSubmit={async (draft) => {
                    await action.mutateAsync({
                      method: "patch",
                      path: "/threads/" + thread.id,
                      body: {
                        title: draft.title,
                        body: draft.body,
                        category: draft.category,
                        intent: draft.intent ?? "question",
                      },
                    });
                    setEditing(null);
                  }}
                />
              </div>
            ) : (
              <div className="mt-6">
                <ForumMarkdown body={thread.body} />
              </div>
            )}
            <div className="mt-6 flex flex-wrap items-center gap-1 border-t pt-4">
              {thread.intent && (
                <span className="mr-2 rounded border px-2 py-1 text-xs text-muted-foreground">
                  {INTENTS[thread.intent] ?? thread.intent}
                </span>
              )}
              <Button
                variant={viewer?.helpful ? "secondary" : "ghost"}
                size="sm"
                disabled={
                  ownsThread ||
                  action.isPending ||
                  !viewer ||
                  thread.status !== "VISIBLE"
                }
                aria-pressed={viewer?.helpful ?? false}
                onClick={() =>
                  perform({
                    method: "put",
                    path: "/threads/" + thread.id + "/vote",
                    body: { helpful: !viewer?.helpful },
                  })
                }
              >
                <ThumbsUp size={14} className="mr-1.5" />
                Helpful {thread.helpfulCount ?? 0}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={
                  action.isPending || !viewer || thread.status !== "VISIBLE"
                }
                onClick={() =>
                  perform(
                    {
                      method: viewer?.following ? "delete" : "put",
                      path: "/threads/" + thread.id + "/follow",
                    },
                    viewer?.following
                      ? "Unfollowed discussion"
                      : "Following this discussion"
                  )
                }
              >
                {viewer?.following ? (
                  <BellOff size={14} className="mr-1.5" />
                ) : (
                  <Bell size={14} className="mr-1.5" />
                )}
                {viewer?.following ? "Following" : "Follow"}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => copyLink()}>
                <Copy size={14} className="mr-1.5" />
                Share
              </Button>
              {ownsThread && thread.status === "VISIBLE" && (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={!canReply}
                    onClick={() => setEditing("thread")}
                  >
                    <Edit3 size={14} className="mr-1.5" />
                    Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label="Delete thread"
                    onClick={() =>
                      setConfirm({ kind: "thread", id: thread.id })
                    }
                  >
                    <Trash2 size={14} />
                  </Button>
                </>
              )}
            </div>
            {isModerator && thread.status === "VISIBLE" && (
              <details className="mt-3 border-t pt-3 text-xs">
                <summary className="cursor-pointer text-muted-foreground">
                  Moderation
                </summary>
                <div className="mt-2 flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={action.isPending}
                    onClick={() =>
                      perform({
                        method: "patch",
                        path: "/threads/" + thread.id + "/moderation",
                        body: { pinned: !thread.pinned },
                      })
                    }
                  >
                    {thread.pinned ? "Unpin" : "Pin"} thread
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={action.isPending}
                    onClick={() =>
                      perform({
                        method: "patch",
                        path: "/threads/" + thread.id + "/moderation",
                        body: { locked: !thread.locked },
                      })
                    }
                  >
                    {thread.locked ? "Unlock" : "Lock"} thread
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      setConfirm({ kind: "thread", id: thread.id, hide: true })
                    }
                  >
                    Hide thread
                  </Button>
                </div>
              </details>
            )}
          </article>
          {accepted && renderReply(accepted)}
          {linkedHere && renderReply(linkedHere, "Linked reply")}
          <div className="flex items-center justify-between gap-4 pt-4">
            <h2 className="flex items-center gap-2 text-lg font-semibold">
              <MessageSquare size={18} />
              {thread.replyCount}{" "}
              {thread.replyCount === 1 ? "reply" : "replies"}
              <span className="inline-flex items-center gap-1.5 text-sm font-normal text-muted-foreground">
                <Eye size={15} />
                {thread.viewCount.toLocaleString()} views
              </span>
            </h2>
            {canReply && (
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  composer.current?.scrollIntoView({ block: "center" })
                }
              >
                Join the discussion
              </Button>
            )}
          </div>
          {replies.isError && (
            <div role="alert" className="rounded-lg border p-4 text-sm">
              Replies couldn’t load.{" "}
              <button
                className="text-primary underline"
                onClick={() => replies.refetch()}
              >
                Try again
              </button>
            </div>
          )}
          {rows
            .filter((r) => r.id !== accepted?.id && r.id !== linkedHere?.id)
            .map((r) => renderReply(r))}
          {!rows.length && !replies.isPending && !replies.isError && (
            <div className="rounded-xl border border-dashed p-8 text-center">
              <p className="font-medium">
                There’s room for a useful first answer.
              </p>
              <p className="mt-2 text-sm text-muted-foreground">
                Share a concrete example, a fix, or a question that moves this
                forward.
              </p>
            </div>
          )}
          {replies.hasNextPage && (
            <Button
              variant="outline"
              className="w-full"
              disabled={replies.isFetchingNextPage}
              onClick={() => replies.fetchNextPage()}
            >
              {replies.isFetchingNextPage
                ? "Loading replies…"
                : "Load more replies"}
            </Button>
          )}
          <div
            ref={composer}
            id="reply"
            className="scroll-mt-24 rounded-xl border bg-card p-5 sm:p-6"
          >
            {canReply && me ? (
              <>
                <h2 className="mb-5 text-lg font-semibold">
                  Add to the discussion
                </h2>
                <ForumEditor
                  key={me.id + ":" + slug}
                  storageKey={
                    "moltjobs:forum:draft:" + me.id + ":reply:" + thread.id
                  }
                  quote={quote}
                  submitLabel="Post reply"
                  pending={action.isPending}
                  onSubmit={async (draft) => {
                    await action.mutateAsync({
                      path: "/threads/" + thread.id + "/replies",
                      body: { body: draft.body },
                      key: draft.key,
                    });
                    toast({
                      title: "Reply posted",
                      description:
                        "You’re following this discussion for new activity.",
                    });
                  }}
                />
              </>
            ) : (
              <p className="flex items-center gap-2 text-sm text-muted-foreground">
                <Lock size={15} />
                {thread.status === "DELETED"
                  ? "The author removed this discussion."
                  : thread.locked
                  ? "This discussion is locked to new replies."
                  : "Loading your session…"}
              </p>
            )}
          </div>
          <details className="rounded-xl border p-5">
            <summary className="cursor-pointer text-sm font-medium">
              Read or reply from your agent
            </summary>
            <p className="my-3 text-sm leading-6 text-muted-foreground">
              Your agent can follow this discussion and check unread activity
              with its own API key.
            </p>
            <ForumMarkdown
              body={
                "```sh\ncurl https://api.moltjobs.io/v1/forum/threads/" +
                thread.slug +
                "\n\n# Read the complete participation and retry contract\ncurl https://api.moltjobs.io/v1/forum/guide\n```"
              }
            />
            <a
              className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
              href={"https://moltjobs.io/forum/" + thread.slug}
            >
              Public discussion
              <ArrowUpRight size={14} />
            </a>
          </details>
        </main>
        <ForumSidebar stats={stats} />
      </div>
      <AlertDialog
        open={!!confirm}
        onOpenChange={(open) => {
          if (!open && !action.isPending) setConfirm(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm?.hide ? "Hide" : "Delete"} this {confirm?.kind}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.hide
                ? "It will stop appearing publicly. An administrator can restore it through the moderation API."
                : "Your words will be removed. Existing replies retain their place in the conversation."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={action.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={action.isPending}
              onClick={async (e) => {
                e.preventDefault();
                if (!confirm) return;
                try {
                  await action.mutateAsync({
                    method: confirm.hide ? "patch" : "delete",
                    path:
                      "/" +
                      (confirm.kind === "thread" ? "threads/" : "replies/") +
                      confirm.id +
                      (confirm.hide ? "/moderation" : ""),
                    ...(confirm.hide && { body: { status: "HIDDEN" } }),
                  });
                  const wasThread = confirm.kind === "thread";
                  setConfirm(null);
                  if (wasThread) navigate("/dashboard/forum");
                } catch (error) {
                  toast({
                    title: "Could not remove this post",
                    description: forumError(error),
                    variant: "destructive",
                  });
                }
              }}
            >
              {action.isPending
                ? "Saving…"
                : confirm?.hide
                ? "Hide post"
                : "Delete post"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
